import {
  Button,
  Flex,
  Group,
  Menu,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowUp,
  IconClipboardList,
  IconFile,
  IconFolder,
  IconFolderPlus,
  IconNote,
  IconPlus,
} from "@tabler/icons-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import { FancyLoader } from "@/components/FancyLoader";
import { FileEditor } from "@/components/FileEditor";
import { Header } from "@/components/Header";
import { LibraryItem } from "@/components/LibraryItem";
import { SurveyEditor } from "@/components/SurveyEditor";
import { TextNoteEditor } from "@/components/TextNoteEditor";
import { TrainerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";

export const FileEdit: React.FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const fileQuery = client.useQuery(
    "get",
    "/universal/files/{fileId}",
    {
      params: { path: { fileId } },
    },
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  if (fileQuery.isLoading) {
    return <FancyLoader />;
  }

  if (fileQuery.isError || !fileQuery.data) {
    return (
      <Text c="red.8">
        Failed to load file details. Please try again later.
      </Text>
    );
  }

  // Check the file type to determine the appropriate editor
  const fileType = fileQuery.data.type;

  if (fileType === "NOTE") {
    return (
      <TextNoteEditor
        file={fileQuery.data}
        folderIdParam={fileQuery.data.parentFolderId}
      />
    );
  }

  if (fileType === "SURVEY") {
    return (
      <SurveyEditor
        file={fileQuery.data}
        folderIdParam={fileQuery.data.parentFolderId}
      />
    );
  }

  return <FileEditor file={fileQuery.data} />;
};

export const FolderEdit: React.FC = () => {
  const { folderId } = useParams<{ folderId: string }>();
  const { navigate } = useNavigator();

  // Get folder listing so we can find the specific folder
  const listFiles = client.useQuery(
    "get",
    "/universal/files/",
    {},
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  // Folder update mutation
  const updateFolder = client.useMutation(
    "post",
    "/universal/folders/{folderId}",
  );

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // Load folder data
  useEffect(() => {
    if (listFiles.data && folderId) {
      const folder = listFiles.data.folders.find((f) => f.id === folderId);
      if (folder) {
        setName(folder.name);
        setDescription(folder.description || "");
      }
    }
  }, [listFiles.data, folderId]);

  if (listFiles.isLoading) {
    return <FancyLoader />;
  }

  if (listFiles.isError || !listFiles.data) {
    return (
      <Text c="red.8">
        Failed to load folder details. Please try again later.
      </Text>
    );
  }

  // Check if folder exists
  const folder = listFiles.data.folders.find((f) => f.id === folderId);
  if (!folder) {
    return (
      <Text c="red.8">
        Folder not found. It may have been deleted or moved.
      </Text>
    );
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    try {
      await updateFolder.mutateAsync({
        params: {
          path: { folderId },
        },
        body: {
          name,
          description,
        },
      });
      navigate({ path: TrainerRoutes.LIBRARY.LIST });
    } catch (error) {
      console.error("Error updating folder:", error);
      setFormError("Failed to update folder. Please try again.");
    }
  };

  return (
    <Stack>
      <Header title="Edit Folder" />
      <form onSubmit={onSubmit}>
        <Stack>
          <TextInput
            label="Folder Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Textarea
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autosize
            minRows={3}
            placeholder="Enter folder description (optional)"
          />

          {formError && (
            <Text color="red" size="sm">
              {formError}
            </Text>
          )}

          <Group justify="flex-end" mt="md">
            <Button
              variant="outline"
              onClick={() => navigate({ path: TrainerRoutes.LIBRARY.LIST })}
            >
              Cancel
            </Button>
            <Button type="submit" loading={updateFolder.isPending}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
};

export const Library: React.FC = () => {
  const { navigator, navigate } = useNavigator();
  const { folderId } = useParams<{ folderId: string }>();
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(
    folderId || null,
  );
  const [_currentFolderName, setCurrentFolderName] = useState<string | null>(
    null,
  );
  const [
    createFolderOpened,
    { open: openCreateFolder, close: closeCreateFolder },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModalRaw },
  ] = useDisclosure(false);

  // Custom close handler to reset state
  const closeDeleteModal = () => {
    closeDeleteModalRaw();
    setDeleteItemId(null);
    setDeleteItemType(null);
  };

  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [deleteItemType, setDeleteItemType] = useState<
    "file" | "folder" | null
  >(null);

  const listFiles = client.useQuery(
    "get",
    "/universal/files/",
    {},
    {
      staleTime: 0,
      gcTime: 0,
    },
  );
  const deleteFile = client.useMutation("delete", "/universal/files/{fileId}");
  const createFolder = client.useMutation("post", "/universal/folders");
  const deleteFolder = client.useMutation(
    "delete",
    "/universal/folders/{folderId}",
  );

  const createFolderForm = useForm({
    initialValues: {
      name: "",
      description: "",
    },
    validate: {
      name: (value) => (value.trim() ? null : "Folder name is required"),
    },
  });

  // Construct a folder path array to display breadcrumb navigation
  const [folderPath, setFolderPath] = useState<
    Array<{ id: string; name: string }>
  >([]);

  useEffect(() => {
    if (!listFiles.data || !currentFolderId) {
      setCurrentFolderName(null);
      setFolderPath([]);
      return;
    }

    const folder = listFiles.data.folders.find((f) => f.id === currentFolderId);
    setCurrentFolderName(folder?.name || null);

    // Build folder path recursively
    const buildFolderPath = (
      folderId: string,
      path: Array<{ id: string; name: string }> = [],
    ): Array<{ id: string; name: string }> => {
      const folder = listFiles.data?.folders.find((f) => f.id === folderId);
      if (!folder) return path;

      const newPath = [{ id: folder.id, name: folder.name }, ...path];

      if (folder.parentFolderId) {
        return buildFolderPath(folder.parentFolderId, newPath);
      }

      return newPath;
    };

    setFolderPath(buildFolderPath(currentFolderId));
  }, [currentFolderId, listFiles.data]);

  const handleCreateFolder = async (values: typeof createFolderForm.values) => {
    try {
      await createFolder.mutateAsync({
        body: {
          name: values.name,
          description: values.description || undefined,
          parentFolderId: currentFolderId,
        },
      });
      createFolderForm.reset();
      closeCreateFolder();
      await listFiles.refetch();
    } catch (error) {
      console.error("Error creating folder:", error);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      await deleteFolder.mutateAsync({
        params: {
          path: { folderId },
          query: { deleteAllContent: false },
        },
      });
      await listFiles.refetch();
      closeDeleteModal();
      if (currentFolderId === folderId) {
        setCurrentFolderId(null);
      }
    } catch (error) {
      console.error("Error deleting folder:", error);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteFile.mutateAsync({
        params: { path: { fileId } },
      });
      await listFiles.refetch();
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  const handleDelete = (id: string, isFolder: boolean) => {
    setDeleteItemId(id);
    setDeleteItemType(isFolder ? "folder" : "file");
    openDeleteModal();
  };

  if (!listFiles.data) {
    return <FancyLoader />;
  }

  const currentFolders = listFiles.data.folders.filter(
    (folder) => folder.parentFolderId === currentFolderId,
  );
  const currentFiles = listFiles.data.files.filter(
    (file) => file.parentFolderId === currentFolderId,
  );

  return (
    <>
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title="Confirm Deletion"
        size="md"
      >
        <Stack>
          {deleteItemType === "file" && deleteItemId && (
            <Text>
              Are you sure you want to delete the file{" "}
              <strong>
                &quot;
                {listFiles.data?.files.find((f) => f.id === deleteItemId)?.name}
                &quot;
              </strong>
              ?
            </Text>
          )}

          {deleteItemType === "folder" && deleteItemId && (
            <>
              <Text>
                Are you sure you want to delete the folder{" "}
                <strong>
                  &quot;
                  {
                    listFiles.data?.folders.find((f) => f.id === deleteItemId)
                      ?.name
                  }
                  &quot;
                </strong>
                ?
              </Text>
              <Text size="sm" c="blue" mt="xs">
                Note: Any files inside this folder will be moved to the root
                level of your library and will not be deleted.
              </Text>
            </>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button
              variant="filled"
              color="red"
              onClick={() => {
                if (deleteItemId) {
                  if (deleteItemType === "file") {
                    handleDeleteFile(deleteItemId);
                  } else if (deleteItemType === "folder") {
                    handleDeleteFolder(deleteItemId);
                  }
                }
              }}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={createFolderOpened}
        onClose={closeCreateFolder}
        title="Create New Folder"
      >
        <form onSubmit={createFolderForm.onSubmit(handleCreateFolder)}>
          <Stack>
            <TextInput
              label="Name"
              placeholder="Enter folder name"
              {...createFolderForm.getInputProps("name")}
              required
            />
            <Textarea
              label="Description"
              placeholder="Enter folder description (optional)"
              {...createFolderForm.getInputProps("description")}
              autosize
              minRows={3}
            />
            <Group justify="flex-end">
              <Button
                variant="light"
                onClick={closeCreateFolder}
                disabled={createFolder.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" loading={createFolder.isPending}>
                Create Folder
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Stack>
        <Flex justify="space-between" align="center">
          <Stack gap="xs">
            <Title order={1}>Library</Title>
            {currentFolderId && (
              <>
                <Group gap="xs">
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => {
                      setCurrentFolderId(null);
                      navigate({
                        path: TrainerRoutes.LIBRARY.LIST,
                      });
                    }}
                  >
                    Root
                  </Button>

                  {folderPath.map((folder, _index) => (
                    <Group gap={4} key={folder.id}>
                      <Text size="xs" c="dimmed">
                        /
                      </Text>
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => {
                          if (folder.id !== currentFolderId) {
                            setCurrentFolderId(folder.id);
                            navigate({
                              path: TrainerRoutes.LIBRARY.FOLDER,
                              params: {
                                folderId: folder.id,
                              },
                            });
                          }
                        }}
                        styles={{
                          root: {
                            fontWeight:
                              folder.id === currentFolderId ? "bold" : "normal",
                          },
                        }}
                      >
                        {folder.name}
                      </Button>
                    </Group>
                  ))}
                </Group>

                <Group gap="xs">
                  <Button
                    variant="subtle"
                    leftSection={<IconArrowUp size="1em" />}
                    onClick={() => {
                      const currentFolder = listFiles.data?.folders.find(
                        (f) => f.id === currentFolderId,
                      );
                      const parentFolderId = currentFolder?.parentFolderId;
                      setCurrentFolderId(parentFolderId || null);

                      if (parentFolderId) {
                        navigate({
                          path: TrainerRoutes.LIBRARY.FOLDER,
                          params: {
                            folderId: parentFolderId,
                          },
                        });
                      } else {
                        navigate({
                          path: TrainerRoutes.LIBRARY.LIST,
                        });
                      }
                    }}
                  >
                    Parent Folder
                  </Button>
                </Group>
              </>
            )}
          </Stack>
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Button
                rightSection={<IconPlus />}
                variant="outline"
                color="black"
              >
                Add
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconFile size="1em" />}
                onClick={() => {
                  navigate({
                    path: TrainerRoutes.LIBRARY.UPLOAD,
                    params: {
                      folderId: currentFolderId || "root",
                    },
                  });
                }}
              >
                File
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFolderPlus size="1em" />}
                onClick={openCreateFolder}
              >
                Folder
              </Menu.Item>
              <Menu.Item
                leftSection={<IconNote size="1em" />}
                onClick={() => {
                  navigate({
                    path: TrainerRoutes.LIBRARY.NEW_TEXT_NOTE,
                    params: {
                      folderId: currentFolderId || "root",
                    },
                  });
                }}
              >
                Text Note
              </Menu.Item>
              <Menu.Item
                leftSection={<IconClipboardList size="1em" />}
                onClick={() => {
                  navigate({
                    path: TrainerRoutes.LIBRARY.NEW_SURVEY,
                    params: {
                      folderId: currentFolderId || "root",
                    },
                  });
                }}
              >
                Survey
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>

        {currentFolders.map((folder) => (
          <LibraryItem
            key={folder.id}
            item={folder}
            kind="folder"
            onDelete={(id) => handleDelete(id, true)}
            onFolderClick={(id) => {
              setCurrentFolderId(id);
              navigate({
                path: TrainerRoutes.LIBRARY.FOLDER,
                params: { folderId: id },
              });
            }}
          />
        ))}

        {currentFiles.map((file) => (
          <LibraryItem
            key={file.id}
            item={file}
            kind="file"
            onDelete={(id) => handleDelete(id, false)}
            onFileClick={navigator({
              path: UniversalRoutes.FILE_PREVIEW,
              params: { fileId: file.id },
            })}
          />
        ))}

        {currentFiles.length === 0 && currentFolders.length === 0 && (
          <Paper p="xl" withBorder>
            <Stack align="center" py="xl">
              <IconFolder size={48} opacity={0.5} />
              <Text size="lg" fw={500}>
                This folder is empty
              </Text>
              <Text c="dimmed" ta="center">
                Start by creating a new folder or uploading files to organize
                your library
              </Text>
              <Group mt="md">
                <Button
                  leftSection={<IconFolderPlus size="1.2em" />}
                  onClick={openCreateFolder}
                >
                  New Folder
                </Button>
                <Button
                  leftSection={<IconFile size="1.2em" />}
                  onClick={() => {
                    navigate({
                      path: TrainerRoutes.LIBRARY.UPLOAD,
                      params: {
                        folderId: currentFolderId || "root",
                      },
                    });
                  }}
                >
                  Upload File
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}
      </Stack>
    </>
  );
};
